import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import Tabs from 'react-bootstrap/Tabs';
import { Col, Row, Tab } from 'react-bootstrap';
import InputGroup from 'react-bootstrap/InputGroup';
import BzDropdownButton, { DropdownButtonItem } from 'components/buttons/BzDropdownButton';
import RequestMatchersList, {
  getUniqueIndex,
  RequestMatcherItem,
} from 'components/transactionEditor/RequestMatchersList';
import RequestCredentials from 'components/transactionEditor/RequestCredentials';
import BodyMatchersList, { BodyMatcherItem, XmlNamespaceItem } from 'components/transactionEditor/BodyMatchersList';
import { CallbackFn, useFormInput, Validator, ValidatorType } from 'hooks/useFormInput';
import BzFormControlWithHook from 'components/forms/BzFormControlWithHook';
import i18n from 'i18n';
import ParameterOptions from 'components/ui/hyperLink/ParameterOptions';
import { Credentials, Matcher, MatcherDsl } from 'types/rest/dslTypes';
import HttpMethodDropdown from 'components/buttons/HttpMethodDropdown';
import { handleNameSpaceChange } from '../../../../../utils/mockServiceUtils';
import { useIsMount } from '../../../../../hooks/useIsMountHook';
import { TransactionType } from '../../../../../types/rest/assetCatalogTypes';
import AiAssistantModal from 'views/AssetCatalog/AiAssistant/AiAssistantModal';
import { getAccountAiConsent } from '../../../../../utils/bzmUserConfigUtil';
import { createAiAssistantIcon } from '../../../AiAssistant/aiAssistantUtil';

interface RequestTabProps {
  state: RequestTabState;
  updateState: (state: RequestTabState) => void;
  isReadOnly?: boolean;
  isWizardModal?: boolean;
  insideSandBox?: boolean;
  type: TransactionType;
  transactionId?: number;
}

export interface RequestTabState {
  method: string;
  host: string;
  path: string;
  url: MatcherDsl;
  headers: RequestMatcherItem[];
  queryParams: RequestMatcherItem[];
  cookies: RequestMatcherItem[];
  credentials: Credentials | null;
  body: BodyMatcherItem[];
  legacySampleBody: string | null;
  valid: boolean;
  dirty: boolean;
  validate: ((value: string) => boolean) | null;
  [key: string]:
    | ((...args) => void)
    | string
    | RequestMatcherItem
    | RequestMatcherItem[]
    | BodyMatcherItem[]
    | MatcherDsl
    | Credentials
    | boolean
    | null;
}

export const title = (title: string, param: string | number | null) => (
  <React.Fragment>
    {title}
    {_.isNull(param) ? '' : <span className="text-muted"> ({param})</span>}
  </React.Fragment>
);

const RequestTab = (props: RequestTabProps) => {
  const isMount = useIsMount();
  const [showAiAssistant, setShowAiAssistant] = useState(false);
  const [aiAssistantCallerId, setAiAssistantCallerId] = useState<string>('');
  const [aiSelectionRange, setAiSelectionRange] = useState<{ start: number; end: number } | undefined>(undefined);
  const urlInputRef = React.useRef<HTMLInputElement>(null);

  // Re-apply / clear URL input selection while the AI modal is open
  useEffect(() => {
    const el = urlInputRef.current;
    if (!el) return;
    if (showAiAssistant && aiSelectionRange && aiSelectionRange.start !== aiSelectionRange.end) {
      el.focus();
      el.setSelectionRange(aiSelectionRange.start, aiSelectionRange.end);
    } else if (!showAiAssistant) {
      el.setSelectionRange(0, 0);
    }
  }, [showAiAssistant]);

  // Stable callerId for the URL field scoped to this transaction.
  // For saved transactions we use the real ID; for new ones we use a per-mount
  // timestamp so each new transaction always starts with a fresh conversation.
  const urlCallerId = React.useRef(
    props.transactionId !== undefined && props.transactionId !== -1
      ? `qa-input-url-${props.transactionId}`
      : `qa-input-url-new-${Date.now()}`,
  ).current;
  const updateState = (field: string, value: any, ignoreDirty?: boolean) => {
    const updatedState = _.cloneDeep(props.state);
    _.set(updatedState, field, value);
    updatedState.valid = urlInput.valid;
    if (!ignoreDirty) {
      updatedState.dirty = true;
    }
    props.updateState(updatedState);
  };

  const updateQueryParamsState = (queryParams: RequestMatcherItem[], url: string) => {
    const updatedState = _.cloneDeep(props.state);
    updatedState.dirty = true;
    _.set(updatedState, 'url.matchingValue', url);
    _.set(updatedState, 'queryParams', queryParams);
    props.updateState(updatedState);
  };

  const parseQueryParams = (url: string): [RequestMatcherItem[], string] => {
    let queryString: string = url.split('?')[1];

    if (_.isEmpty(queryString)) {
      return [[], url];
    } else {
      queryString = queryString.split('#')[0];
    }

    let idsIndexCounter = getUniqueIndex(props.state.queryParams);
    if (idsIndexCounter == null) {
      idsIndexCounter = 0;
    }

    const finalParams: RequestMatcherItem[] = [];
    const filteredParams = props.state.queryParams.filter(param => param.isParsedFromUrl !== true);
    const queryParams: string[] = queryString.split('&');

    for (const queryParam of queryParams) {
      const processedParam = queryParam.split('=');
      const paramKey = processedParam[0];
      const paramValue = processedParam[1];
      //adjust url string, cut parsed params
      if (_.isEmpty(paramValue)) {
        url = queryParam.includes('=') ? url.replace(paramKey + '=', '') : url.replace(paramKey, '');
      } else {
        url = url.replace(paramKey + '=' + paramValue, '');
      }

      finalParams.push({
        id: 'matchers-query-params-' + idsIndexCounter++,
        name: paramKey,
        operator: 'equals',
        value: paramValue,
        isParsedFromUrl: true,
      });
    }

    url = url.replace('?', '');
    while (url.includes('&')) {
      url = url.replace('&', '');
    }

    return [finalParams.concat(filteredParams), url];
  };
  const [tabRequestState, setTabRequestState] = useState('headers');
  const [urlContentError, setUrlContentError] = useState(false);
  const [shouldOverrideUrl, setShouldOverrideUrl] = useState(false);

  const urlMatcherItems: DropdownButtonItem[] = [
    {
      key: 'equals_url',
      title: i18n.t('transactions:editor.equalsCaps'),
    },
    {
      key: 'matches_url',
      title: i18n.t('transactions:editor.matchesRegexCaps'),
    },
  ];

  const nonEmptyListMessage = i18n.t('transactions:editor.nonEmptyListMessage');
  const selectedUrlMatcherButton = urlMatcherItems.find(
    i => i.key === props.state.url.matcherName,
  ) as DropdownButtonItem;

  // Show AI assistant icon only for regex URL operator and when user has given consent
  const shouldShowAiIcon = getAccountAiConsent() && props.state.url.matcherName === Matcher.matches_url;

  let validators: Validator[] = [
    {
      type: ValidatorType.REQUIRED,
      invalidMessage:
        props.isWizardModal === true
          ? i18n.t('transactions:editor.pathRequired')
          : i18n.t('transactions:editor.urlRequired'),
    },
    {
      type: ValidatorType.INVALID_LEADING_SPACES,
      invalidMessage:
        props.isWizardModal === true
          ? i18n.t('transactions:editor.pathIsInvalid')
          : i18n.t('transactions:editor.urlIsInvalid'),
    },
  ];
  if (props.isWizardModal === true) {
    validators = [
      ...validators,
      {
        type: ValidatorType.INVALID_INIT_CHARACTER,
        value: '/',
        invalidMessage: i18n.t('transactions:editor.slashRequired'),
      },
    ];
  }

  const urlInput = useFormInput(
    props.state.url.matchingValue,
    (fn: CallbackFn) => {
      if (selectedUrlMatcherButton.title === i18n.t('transactions:editor.equalsCaps')) {
        if (fn.onPaste && fn.value.toString().includes('?')) {
          setShouldOverrideUrl(true);
          const parsedParamsAndUrl = parseQueryParams(fn.value.toString());
          if (parsedParamsAndUrl[0].length) {
            updateQueryParamsState(parsedParamsAndUrl[0], parsedParamsAndUrl[1]);
            setTabRequestState('query-params');
          }
        } else {
          //onChange
          if (fn.value.toString().includes('?')) {
            setUrlContentError(true);
            setTabRequestState('query-params');
          } else {
            setUrlContentError(false);
            updateState('url.matchingValue', fn.value);
          }
        }
      } else {
        updateState('url.matchingValue', fn.value);
      }
    },
    ...validators,
  );

  if (shouldOverrideUrl) {
    setShouldOverrideUrl(false);
    urlInput.overrideValue(props.state.url.matchingValue);
  }

  useEffect(() => {
    if (isMount) {
      //Validate on mount
      updateState('validate', urlInput.validate, true);
    }
  }, []);

  useEffect(() => {
    if (urlInput.value !== props.state.url.matchingValue) {
      urlInput.overrideValue(props.state.url.matchingValue);
    }
  }, [props.state.url.matchingValue]);

  const addCredentials = () => {
    const credentials = {
      username: '',
      password: '',
    };
    updateState('credentials', credentials);
  };

  const handleAiAssistantClick = (callerId: string) => {
    setAiAssistantCallerId(callerId);
    setShowAiAssistant(true);
  };

  const captureUrlSelection = () => {
    const el = urlInputRef.current;
    if (el) {
      setAiSelectionRange({ start: el.selectionStart || 0, end: el.selectionEnd || 0 });
    } else {
      setAiSelectionRange(undefined);
    }
  };

  const aiAssistant = (callerId: string) => (
    <div onMouseDown={captureUrlSelection} onClick={() => handleAiAssistantClick(callerId)}>
      {createAiAssistantIcon()}
    </div>
  );

  return (
    <div className="bg-light p-4">
      <Row className="mb-4">
        <Col md={12}>
          {props.isWizardModal === true ? (
            <>{i18n.t('wizard:details.behavior.requestMatcher')}</>
          ) : (
            <ParameterOptions />
          )}
        </Col>
      </Row>
      <label htmlFor="qa-input-url">
        {props.isWizardModal === true ? i18n.t('transactions:editor.relativePath') : i18n.t('transactions:editor.url')}
      </label>
      <Row className="mb-3">
        <Col md={12}>
          <InputGroup>
            <HttpMethodDropdown isReadOnly={props.isReadOnly} method={props.state.method} updateState={updateState} />
            <BzFormControlWithHook
              disabled={props.isReadOnly}
              useFormInputHook={urlInput}
              autoComplete={'off'}
              type="text"
              name="url.matchingValue"
              placeholder={
                props.isWizardModal === true
                  ? i18n.t('transactions:editor.enterPath')
                  : i18n.t('transactions:editor.enterUrl')
              }
              required={true}
              id="qa-input-url"
              hideErrors={true}
              icon={shouldShowAiIcon ? aiAssistant(urlCallerId) : undefined}
              ref={urlInputRef}
            />
            {props.isWizardModal !== true && (
              <BzDropdownButton
                width="120px"
                variant="secondary"
                className="input-group-append ml-3"
                id="url-matcher"
                dropdownItems={urlMatcherItems}
                center={true}
                selectedItem={selectedUrlMatcherButton}
                onSelectedItem={(item: DropdownButtonItem) => updateState('url.matcherName', item.key)}
                alignRight={true}
              />
            )}
          </InputGroup>
          {!urlInput.valid && (urlInput.dirty || urlInput.validating) && (
            <div className="invalid-feedback">{urlInput.invalidMessage}</div>
          )}
          {urlContentError && (
            <div className="invalid-feedback">{i18n.t('transactions:editor.queryParamsInUrlWarn')}</div>
          )}
          {props.isWizardModal === true &&
            !((!urlInput.valid && (urlInput.dirty || urlInput.validating)) || urlContentError) && (
              <div className="text-muted small mt-2">{i18n.t('wizard:details.behavior.pathDescription')}</div>
            )}
        </Col>
      </Row>
      <Tabs id="qa-tabs-request" activeKey={tabRequestState} onSelect={(key: string) => setTabRequestState(key)}>
        <Tab title={title(i18n.t('transactions:editor.headersTitle'), props.state.headers.length)} eventKey="headers">
          <RequestMatchersList
            showIsRequiredCheckbox={true}
            isReadOnly={props.isReadOnly}
            onListChange={(items: RequestMatcherItem[]) => updateState('headers', items)}
            matcherList={props.state.headers}
            id="matchers-header"
            emptyListMessage={i18n.t('transactions:editor.emptyHeaderListMessage')}
            nonEmptyListMessage={nonEmptyListMessage}
            matcherTitle={i18n.t('transactions:editor.headerMatcherTitle')}
            isWizardModal={props.isWizardModal}
            aiAssistantTitle="Request Matcher Header"
            transactionId={props.transactionId}
          />
        </Tab>
        <Tab
          title={title(i18n.t('transactions:editor.queryParamsTitle'), props.state.queryParams.length)}
          eventKey="query-params"
        >
          <RequestMatchersList
            isReadOnly={props.isReadOnly}
            onListChange={(items: RequestMatcherItem[]) => updateState('queryParams', items)}
            matcherList={props.state.queryParams}
            id="matchers-query-params"
            emptyListMessage={i18n.t('transactions:editor.emptyQueryListMessage')}
            nonEmptyListMessage={i18n.t('transactions:editor.nonEmptyListMessageOptional')}
            matcherTitle={i18n.t('transactions:editor.queryMatcherTitle')}
            isWizardModal={props.isWizardModal}
            showIsRequiredCheckbox={true}
            aiAssistantTitle="Request Query Params"
            transactionId={props.transactionId}
          />
        </Tab>
        {props.isWizardModal !== true && props.insideSandBox !== true && (
          <Tab title={title(i18n.t('transactions:editor.cookiesTitle'), props.state.cookies.length)} eventKey="cookies">
            <RequestMatchersList
              isReadOnly={props.isReadOnly}
              onListChange={(items: RequestMatcherItem[]) => updateState('cookies', items)}
              matcherList={props.state.cookies}
              id="matchers-cookies"
              emptyListMessage={i18n.t('transactions:editor.emptyCookieListMessage')}
              nonEmptyListMessage={nonEmptyListMessage}
              matcherTitle={i18n.t('transactions:editor.cookieMatcherTitle')}
              aiAssistantTitle="Request Matcher Cookies"
              transactionId={props.transactionId}
            />
          </Tab>
        )}
        {props.isWizardModal !== true && props.insideSandBox !== true && (
          <Tab
            title={title(
              i18n.t('transactions:editor.credentialsTitle'),
              props.state.credentials && (props.state.credentials.username || props.state.credentials.password)
                ? `${i18n.t('transactions:editor.defined')}`
                : `${i18n.t('transactions:editor.notDefined')}`,
            )}
            eventKey="credentials"
          >
            <RequestCredentials
              id="request-credentials"
              isReadOnly={props.isReadOnly}
              credentials={props.state.credentials}
              onAdd={addCredentials}
              onChange={(name: string, value: string) => updateState('credentials.' + name, value)}
              onDelete={() => updateState('credentials', null)}
            />
          </Tab>
        )}
        <Tab title={title(i18n.t('transactions:editor.bodyTitle'), props.state.body.length)} eventKey="body">
          <BodyMatchersList
            id="body-matchers"
            isReadOnly={props.isReadOnly}
            bodyMatcherList={props.state.body}
            legacySampleBody={props.state.legacySampleBody}
            onListChange={(items: BodyMatcherItem[]) => updateState('body', items)}
            onXmlNamespaceListChange={(items: XmlNamespaceItem[]) =>
              handleNameSpaceChange(items, props.state, props.updateState)
            }
            isWizardModal={props.isWizardModal}
            enableAiAssistant={true}
            transactionId={props.transactionId}
          />
        </Tab>
      </Tabs>
      {showAiAssistant && (
        <AiAssistantModal
          callerId={aiAssistantCallerId}
          updateFunction={value => updateState('url.matchingValue', value)}
          currentValue={props.state.url.matchingValue}
          onClose={() => setShowAiAssistant(false)}
          title="Request Matcher Url"
          selectionRange={aiSelectionRange}
        />
      )}
    </div>
  );
};

export default RequestTab;
